import React, { ReactNode, useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { ConfirmDeletionDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { field3ToFormField, IField3 } from '../../../data-models/field3.data-model';
import {
  createForm,
  createFormFromFieldsArray,
  IForm,
  IFormField,
} from '../../../view-models/form.view-model';
import { ConfigModal, DynamicFormTransformer, useConfigModal } from './ConfigModal';
import { DataWithId, useConfigActions } from './UseConfigActions';
import { ConfigPageContents, ConfigViewMode } from './ConfigPageContents2';

export interface IDeleteConfirmationConfig {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
}

export interface IGenericConfigPageProps<T extends FieldValues & DataWithId> {
  baseTitle: string;
  customToolbarElem?: React.JSX.Element;
  data: T[];
  defaultValues?: Partial<T>;
  defaultViewMode?: ConfigViewMode;
  deleteConfirmation?: (item: T) => IDeleteConfirmationConfig;
  fields: IField3<unknown>[];
  formFields?: IFormField<unknown>[];
  /** Subset of fields shown in the list rows / grid columns; defaults to formFields (modal always uses formFields). */
  rowFields?: IFormField<unknown>[];
  subHeaderElem?: React.JSX.Element;
  isSortable?: boolean;
  itemIsDisabled?: (item: T) => boolean;
  pageTitle: string;
  queryURL: string;
  /** When set, a back chevron is rendered on the left of the page title. */
  onBack?: () => void;
  showCreateButton?: boolean;
  showDeleteButton?: boolean;
  transformBeforeSubmit?: (data: T) => T;
  dynamicFormTransformer?: DynamicFormTransformer<T>;
}
export function GenericConfigPage<T extends FieldValues & DataWithId>(props: IGenericConfigPageProps<T>) {
  const {
    baseTitle,
    customToolbarElem,
    data,
    defaultValues = {},
    defaultViewMode,
    deleteConfirmation,
    fields,
    isSortable,
    itemIsDisabled,
    pageTitle,
    queryURL,
    showCreateButton = true,
    showDeleteButton = true,
    transformBeforeSubmit,
  } = props;
  const { open, handleOpen, handleClose, isEdit } = useConfigModal();
  const { bulkUpdateItems, updateItem, deleteItem } = useConfigActions(queryURL);
  const formFields = props.formFields ? props.formFields : fields.map((field) => field3ToFormField(field));
  const rowFields = props.rowFields ?? formFields;
  const [currentForm, setCurrentForm] = useState(() => createFormFromFieldsArray(formFields));
  const [pendingDeleteItem, setPendingDeleteItem] = useState<T | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const forms = data.map((data) =>
    createFormFromFieldsArray(rowFields, { data, disabled: itemIsDisabled?.(data) ?? false })
  );
  const _updateItem = useCallback(
    async (formData: T) => {
      const formPayload = transformBeforeSubmit?.(formData) ?? formData;
      await updateItem(formPayload, isEdit);
    },
    [isEdit, transformBeforeSubmit, updateItem]
  );
  const _bulkUpdateItems = useCallback(
    async (formsData: T[]) => {
      const payload = formsData.map((formData) => {
        return transformBeforeSubmit?.(formData) ?? formData;
      });

      await bulkUpdateItems(payload);
    },
    [bulkUpdateItems, transformBeforeSubmit]
  );
  const confirmPendingDelete = useCallback(async () => {
    if (!pendingDeleteItem) {
      return;
    }
    setDeleteLoading(true);
    // deleteItem never throws: it reloads the page on success, so resetting only matters on failure
    await deleteItem(pendingDeleteItem);
    setDeleteLoading(false);
    setPendingDeleteItem((current) => (current === pendingDeleteItem ? null : current));
  }, [deleteItem, pendingDeleteItem]);
  const pendingDeleteConfirmation =
    deleteConfirmation && pendingDeleteItem ? deleteConfirmation(pendingDeleteItem) : null;

  return (
    <>
      <ConfigPageContents
        createBtnLabel={`Create ${baseTitle}`}
        customToolbarElem={customToolbarElem}
        subHeaderElem={props.subHeaderElem}
        defaultViewMode={defaultViewMode}
        onCreate={() => {
          setCurrentForm(createForm(createFormFromFieldsArray(formFields, { data: defaultValues })));
          handleOpen(false);
        }}
        onEdit={(item) => {
          setCurrentForm(createForm(createFormFromFieldsArray(formFields, { data: item })));
          handleOpen(true);
        }}
        onDelete={
          showDeleteButton
            ? async (item) => {
                if (deleteConfirmation) {
                  setPendingDeleteItem(item);
                } else {
                  await deleteItem(item);
                }
              }
            : undefined
        }
        onReorder={isSortable ? _bulkUpdateItems : undefined}
        forms={forms}
        title={pageTitle}
        onBack={props.onBack}
        showCreateButton={showCreateButton}
      />
      <ConfigModal
        form={currentForm as IForm<T>}
        onClose={handleClose}
        onSubmit={_updateItem}
        open={open}
        title={isEdit ? `Edit ${baseTitle}` : `Create ${baseTitle}`}
        dynamicFormTransformer={props.dynamicFormTransformer}
      />
      {pendingDeleteConfirmation && (
        <ConfirmDeletionDialog
          open
          title={pendingDeleteConfirmation.title}
          confirmLabel={pendingDeleteConfirmation.confirmLabel}
          loading={deleteLoading}
          onConfirm={confirmPendingDelete}
          onClose={() => {
            // closing cannot abort the request, so ignore it while the delete is in flight
            if (!deleteLoading) {
              setPendingDeleteItem(null);
            }
          }}
        >
          {pendingDeleteConfirmation.body}
        </ConfirmDeletionDialog>
      )}
    </>
  );
}
